import "@crescentParts/Store/AHRISystemReducer";
import "@crescentParts/Store/CrescentPartsTraining/TrainingsReducer";
import "@crescentParts/Store/CustomQuantityBreakPricing/CustomQuantityBreakPricingReducer";
import "@crescentParts/Store/OnlineManufacturerTraining/OnlineManufacturerTrainingsReducer";
import { addPagesFromContext, addWidgetsFromContext } from "@insite/client-framework/Configuration";
import { setPostStyleGuideTheme } from "@insite/client-framework/ThemeConfiguration";
import { TextAreaStyle } from "@insite/mobius/TextArea";
import { InputStyle } from "@insite/mobius/TextField";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { css } from "styled-components";

// For production, include a set of commonly used widgets in the main bundle
// the rest will be split into their own chunks
// Do not do this for the ./Overrides folder
const widgets = IS_PRODUCTION
    ? require.context("./Widgets", true, /(Header|Basic|Common|Footer|SignIn)\/.+?\.tsx$/)
    : require.context("./Widgets", true, /\.tsx$/);
// enable hot module reloading for widgets
const onHotWidgetReplace = addWidgetsFromContext(require.context("./Widgets", true, /\.tsx$/));
if (module.hot) {
    module.hot.accept(widgets.id.toString(), () => onHotWidgetReplace(require.context("./Widgets", true, /\.tsx$/)));
}

// Load all custom pages so they are included in the bundle and enable hot module reloading for them. Do not do this for the ./Overrides folder
// const pages = require.context("./Pages", true, /\.tsx$/);
// const onHotPageReplace = addPagesFromContext(pages);
// if (module.hot) {
//     module.hot.accept(pages.id, () => onHotPageReplace(require.context("./Pages", true, /\.tsx$/)));
// }

// Load all handlers so they are included in the bundle.
const handlers = require.context("./Handlers", true);
handlers.keys().forEach(key => handlers(key));

// in production, the widget extensions are included in the chunks created for widgets
// if (!IS_PRODUCTION) {
// load all widget extensions. They could be loaded individually instead
const widgetExtensions = require.context("./WidgetExtensions", true);
widgetExtensions.keys().forEach(key => widgetExtensions(key));
// }

// Add some post styleguide customizations. These cannot be overridden by the style guide.
setPostStyleGuideTheme({
    breakpoints: {
        keys: ["xs", "sm", "md", "lg", "xl"],
        values: [0, 576, 768, 992, 1200],
        maxWidths: [576, 576, 768, 992, 1440],
    },
    typography: {
        fontFamilyImportUrl:
            'https://fonts.googleapis.com/css2?family=Hind:wght@300;400;500;600&display=swap" rel="stylesheet"',
        body: {
            fontFamily: "Hind",
            size: "1rem",
            weight: 400,
            lineHeight: "24px",
        },
        p: {
            size: "1rem",
            weight: 400,
            lineHeight: "24px",
        },
        headerPrimary: {
            size: "1rem",
            weight: 700,
            lineHeight: "24px",
        },
        headerSecondary: {
            size: "0.875rem", // 14px
            weight: 700,
            lineHeight: "22px",
        },
        headerTertiary: {
            size: "0.875rem", // 14px
            weight: 400,
            lineHeight: "22px",
        },
        h1: {
            size: "2.25rem", // 36px
            weight: 700,
            color: "text.accent",
            lineHeight: "44px",
        },
        h2: {
            size: "1.875rem", // 30px
            weight: 700,
            color: "text.accent",
            lineHeight: "38px",
        },
        h3: {
            size: "1.5rem", // 24px
            weight: 700,
            color: "text.accent",
            lineHeight: "32px",
        },
        h4: {
            size: "1.25rem", // 20px
            weight: 700,
            color: "text.accent",
            lineHeight: "28px",
        },
        h5: {
            size: "1.125rem", // 18px
            weight: 700,
            color: "text.accent",
            lineHeight: "26px",
        },
        h6: {
            size: "1rem",
            weight: 700,
            color: "text.accent",
            lineHeight: "24px",
        },
    },
    pagination: {
        defaultProps: {
            cssOverrides: {
                pagination: css`
                    background-color: ${getColor("secondary")};
                    border-radius: 6px;
                    padding: 9px 24px;
                    label {
                        font-size: 14px;
                    }
                    [class^="PerPageSelect"] {
                        [class^="FormFieldStyle"] {
                            display: flex;
                            align-items: center;
                            label {
                                font-weight: 400;
                                color: ${getColor("text.main")};
                                padding: 0 15px 0 0;
                            }
                            [class^="IconWrapper"] {
                                width: 30px !important;
                                height: 25px !important;
                                right: 28px !important;
                            }
                            select {
                                width: 43px;
                            }
                        }
                    }
                    [class^="SelectStyle"] {
                        color: ${getColor("text.accent")}!important;
                        border-radius: 6px !important;
                        font-size: 14px !important;
                        height: 28px !important;
                        padding: 0 7px 0 7px !important;
                    }
                `,
                currentButton: css`
                    cursor: default;
                    color: ${getProp("theme.colors.primary.contrast")};
                    background-color: ${getProp("theme.colors.primary.main")};
                    border-color: ${getProp("theme.colors.primary.main")};
                    min-height: 24px;
                    width: 24px;
                    height: 24px;
                    font-size: 14px;
                `,
                linkList: css`
                    button {
                        max-width: fit-content;
                        white-space: nowrap;
                        padding: 0px 6px;
                        width: 32px;
                        &:hover {
                            border: none !important;
                        }
                    }
                `,
            },
        },
    },
    formField: {
        defaultProps: {
            labelProps: {
                color: "text.accent",
            },
            cssOverrides: {
                inputSelect: css`
                    font-size: 1rem;
                    &:focus,
                    &:focus-within {
                        border: 2px solid ${getColor("common.border")} !important;
                    }
                `,
                formInputWrapper: css`
                    > ${InputStyle}, > ${TextAreaStyle}, select {
                        &:focus,
                        &:focus-within {
                            border: 2px solid ${getColor("common.border")} !important;
                        }
                    }
                    > div[class^="DateTimePickerStyle"] {
                        > div[class^="react-datetime-picker"] {
                            &:focus,
                            &:focus-within {
                                border: 2px solid ${getColor("common.border")} !important;
                            }
                        }
                    }
                    > div[class^="FileUploadStyle-sc"] {
                        &:focus-within,
                        &:focus {
                            border: 2px solid ${getColor("common.border")} !important;
                        }
                        input {
                            font-size: 1rem;
                            &:focus,
                            &:focus-within {
                                border: 2px solid ${getColor("common.border")} !important;
                            }
                        }
                    }
                    input,
                    select,
                    textarea {
                        &:focus {
                            border: 2px solid ${getColor("common.border")} !important;
                        }
                    }
                `,
            },
        },
    },
    textField: {
        defaultProps: {
            cssOverrides: {
                inputSelect: css`
                    &:focus,
                    &:focus-within {
                        border: 2px solid ${getColor("common.border")} !important;
                    }
                `,
            },
        },
    },
    textArea: {
        defaultProps: {
            cssOverrides: {
                inputSelect: css`
                    &:focus,
                    &:focus-within {
                        border: 2px solid ${getColor("common.border")} !important;
                    }
                `,
            },
        },
    },
    accordion: {
        sectionDefaultProps: {
            headerProps: {
                css: css`
                    button {
                        background-color: ${getColor("secondary")};
                        &:focus {
                            border: 1px solid ${getColor("common.border")};
                        }
                    }
                `,
            },
            titleTypographyProps: {
                variant: "h5",
                color: "text.accent",
                css: css`
                    margin-bottom: 0;
                `,
            },
        },
    },
    dataTable: {
        defaultProps: {
            css: css`
                > thead {
                    > tr {
                        > th {
                            background: ${getColor("secondary")};
                            > span {
                                color: ${getColor("text.accent")};
                            }
                        }
                    }
                }
                > tbody {
                    > tr {
                        &:nth-of-type(even) {
                            background: #ffffff;
                        }
                    }
                }
            `,
        },
    },
    focus: {
        color: "transparent", // no blue borders on link focus
    },
});
